import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatMoney, formatTime12h, formatDateLong } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Check, ArrowRight, Loader2 } from "lucide-react";

const Booking = () => {
  const [searchParams] = useSearchParams();
  const initialPackage = searchParams.get("package") || "individual";

  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(initialPackage);
  const [availability, setAvailability] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/packages", { params: { include_hidden: true } }).then((r) => setPackages(r.data.packages));
    api
      .get("/availability?days=60")
      .then((r) => setAvailability(r.data.slots))
      .finally(() => setLoadingAvail(false));
  }, []);

  useEffect(() => {
    if (searchParams.get("cancelled")) {
      toast.error("Payment cancelled. Your slot is reserved — try again when ready.");
    }
  }, [searchParams]);

  const availableDates = useMemo(
    () => new Set(availability.map((s) => s.date)),
    [availability]
  );

  const timesForDate = useMemo(() => {
    if (!selectedDate) return [];
    const iso = formatLocalISO(selectedDate);
    const found = availability.find((s) => s.date === iso);
    return found ? found.times : [];
  }, [availability, selectedDate]);

  const currentPackage = packages.find((p) => p.id === selectedPkg);
  const isDiscovery = selectedPkg === "discovery_call";
  // Only show discovery_call in picker when explicitly requested via URL
  const visiblePackages = isDiscovery
    ? packages.filter((p) => p.id === "discovery_call")
    : packages.filter((p) => p.id !== "discovery_call");

  const isFormValid =
    selectedDate &&
    selectedTime &&
    form.name.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email);

  const submit = async () => {
    if (!isFormValid || !currentPackage) {
      toast.error("Please complete your details and choose a time.");
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await api.post("/bookings", {
        ...form,
        package_id: selectedPkg,
        session_date: formatLocalISO(selectedDate),
        session_time: selectedTime,
        timezone: "America/New_York",
      });
      const bookingId = bookingRes.data.booking.id;

      const checkoutRes = await api.post("/checkout/session", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-16" data-testid="booking-page">
      <div className="max-w-[1300px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="mb-12">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">
            Book your session
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4F5D4F] leading-[1.05]">
            Bring what you're carrying,<br />
            <em className="italic text-[#B89B63]">and let's slow it down together.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed max-w-2xl">
            Faith &amp; Identity Coaching for the woman ready to hear her own voice.
            Choose the space that fits you today—one session, or a six-month partnership.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* LEFT: package + calendar */}
          <div className="lg:col-span-7 space-y-10">
            {/* Package picker */}
            <div className="bg-white p-8 border border-[#D7B2A3]/50">
              <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-5">
                1. Choose your space
              </p>
              <div className={`grid grid-cols-1 ${isDiscovery ? "" : "sm:grid-cols-3"} gap-4`}>
                {visiblePackages.map((pkg) => {
                  const active = selectedPkg === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPkg(pkg.id)}
                      className={`text-left p-4 border transition-colors ${
                        active
                          ? "border-[#4F5D4F] bg-[#4F5D4F] text-[#F6F1EA]"
                          : "border-[#D7B2A3]/60 text-[#4F5D4F] hover:border-[#4F5D4F]"
                      }`}
                      data-testid={`booking-pkg-${pkg.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-serif text-lg leading-tight">{pkg.name}</p>
                        {active && <Check size={16} className="text-[#B89B63] mt-1 shrink-0" />}
                      </div>
                      <div className="mt-2 flex items-baseline gap-2">
                        {pkg.regular_price && pkg.regular_price > pkg.amount && (
                          <span className={`text-sm line-through ${active ? "text-[#D7B2A3]/80" : "text-[#8C9A88]"}`}>
                            {formatMoney(pkg.regular_price)}
                          </span>
                        )}
                        <span className={`text-2xl font-serif ${active ? "text-[#D7B2A3]" : "text-[#4F5D4F]"}`}>
                          {formatMoney(pkg.amount)}
                        </span>
                      </div>
                      <p className={`text-[10px] tracking-eyebrow uppercase mt-1 ${active ? "text-[#F6F1EA]/70" : "text-[#8C9A88]"}`}>
                        {pkg.id === "individual" ? "60-min private session" : pkg.id === "membership_monthly" ? "Becoming Her · monthly · auto-cancels at 6" : pkg.id === "package_12" ? "Becoming Her · 12 sessions upfront" : "Free discovery call"}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white p-8 border border-[#D7B2A3]/50">
              <div className="flex items-baseline justify-between mb-5">
                <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88]">
                  2. Choose a date & time
                </p>
                <p className="text-xs text-[#8C9A88]">All times shown in ET (Eastern Time)</p>
              </div>

              {loadingAvail ? (
                <div className="flex items-center gap-3 text-[#4F5D4F] py-8">
                  <Loader2 className="animate-spin" size={18} /> Loading availability…
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div data-testid="booking-calendar">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => {
                        setSelectedDate(d);
                        setSelectedTime(null);
                      }}
                      disabled={(date) => {
                        const iso = formatLocalISO(date);
                        return !availableDates.has(iso);
                      }}
                      className="rounded-none p-0"
                    />
                  </div>

                  <div>
                    <p className="font-serif text-2xl text-[#4F5D4F] mb-4">
                      {selectedDate ? formatDateLong(formatLocalISO(selectedDate)) : "Select a date"}
                    </p>
                    {selectedDate && timesForDate.length === 0 && (
                      <p className="text-sm text-[#8C9A88]">No times available on that day.</p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {timesForDate.map((t) => {
                        const active = selectedTime === t;
                        return (
                          <button
                            key={t}
                            onClick={() => setSelectedTime(t)}
                            className={`py-3 px-4 text-sm tracking-wide border transition-colors ${
                              active
                                ? "bg-[#4F5D4F] text-[#F6F1EA] border-[#4F5D4F]"
                                : "border-[#D7B2A3] text-[#4F5D4F] hover:border-[#4F5D4F]"
                            }`}
                            data-testid={`booking-time-${t.replace(":", "")}`}
                          >
                            {formatTime12h(t)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="bg-white p-8 border border-[#D7B2A3]/50 space-y-5">
              <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-2">
                3. Your details
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs tracking-eyebrow uppercase text-[#8C9A88] block mb-2">
                    Full name
                  </label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 focus-visible:ring-[#B89B63] focus-visible:ring-1 h-12"
                    data-testid="booking-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-eyebrow uppercase text-[#8C9A88] block mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 focus-visible:ring-[#B89B63] focus-visible:ring-1 h-12"
                    data-testid="booking-input-email"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-eyebrow uppercase text-[#8C9A88] block mb-2">
                  Phone (optional)
                </label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 focus-visible:ring-[#B89B63] focus-visible:ring-1 h-12"
                  data-testid="booking-input-phone"
                />
              </div>
              <div>
                <label className="text-xs tracking-eyebrow uppercase text-[#8C9A88] block mb-2">
                  Anything Jess should know? (optional)
                </label>
                <Textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 focus-visible:ring-[#B89B63] focus-visible:ring-1"
                  data-testid="booking-input-notes"
                />
              </div>
            </div>
          </div>

          {/* RIGHT: summary */}
          <aside className="lg:col-span-5">
            <div className="bg-[#4F5D4F] text-[#F6F1EA] p-10 sticky top-28" data-testid="booking-summary">
              <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6">
                Your booking
              </p>
              <h3 className="font-serif text-3xl leading-tight">
                {currentPackage?.name || "—"}
              </h3>
              <p className="text-[#F6F1EA]/80 mt-3 font-light leading-relaxed">
                {currentPackage?.description}
              </p>

              <div className="my-8 border-t border-[#F6F1EA]/20" />

              <dl className="space-y-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-[#D7B2A3] tracking-eyebrow uppercase text-xs">Date</dt>
                  <dd data-testid="summary-date" className="text-right">
                    {selectedDate ? formatDateLong(formatLocalISO(selectedDate)) : "Not selected"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#D7B2A3] tracking-eyebrow uppercase text-xs">First session</dt>
                  <dd data-testid="summary-time">
                    {selectedTime ? formatTime12h(selectedTime) : "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#D7B2A3] tracking-eyebrow uppercase text-xs">Sessions</dt>
                  <dd>{currentPackage?.sessions}</dd>
                </div>
              </dl>

              <div className="my-8 border-t border-[#F6F1EA]/20" />

              <div className="flex justify-between items-baseline">
                <span className="text-xs tracking-eyebrow uppercase text-[#D7B2A3]">Total</span>
                <span className="font-serif text-5xl" data-testid="summary-total">
                  {currentPackage ? (currentPackage.amount === 0 ? "Free" : formatMoney(currentPackage.amount)) : "—"}
                </span>
              </div>

              <button
                onClick={submit}
                disabled={!isFormValid || submitting}
                className="group mt-10 w-full inline-flex items-center justify-center gap-3 bg-[#B89B63] text-[#F6F1EA] hover:bg-[#a08754] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-5 text-xs tracking-eyebrow uppercase transition-colors"
                data-testid="booking-checkout-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Securing your slot…
                  </>
                ) : isDiscovery ? (
                  <>
                    Confirm your discovery call
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                ) : (
                  <>
                    Continue to secure payment
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              <p className="text-[10px] tracking-eyebrow uppercase text-[#F6F1EA]/60 text-center mt-4">
                {isDiscovery
                  ? "20 minutes · no cost · no pressure"
                  : "Payment by Stripe · Cancel up to 48h before"}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

function formatLocalISO(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default Booking;
